import type { FC, PointerEvent, ReactNode } from 'react'

import { useRef } from 'react'
import { motion, useSpring } from 'motion/react'
import { cn } from '@/lib/utils'
import { useReduceMotion } from '@/hooks/use-reduce-motion'
import { photos } from '@/constants/content'

interface PortraitProps {
    tone?: 'blaze' | 'cobalt'
    src?: string
    className?: string
}

interface TiltCardProps {
    children: ReactNode
    className?: string
}

// Tilts toward the mouse like a card being picked up. Still on touch screens and for reduced motion.
export const TiltCard: FC<TiltCardProps> = ({ children, className }): ReactNode => {
    const ref = useRef<HTMLDivElement>(null)
    const reduce = useReduceMotion()
    const rotateX = useSpring(0, { stiffness: 180, damping: 18 })
    const rotateY = useSpring(0, { stiffness: 180, damping: 18 })

    const handleMove = (event: PointerEvent<HTMLDivElement>) => {
        if (reduce || event.pointerType !== 'mouse' || !ref.current) return

        const rect = ref.current.getBoundingClientRect()
        const px = (event.clientX - rect.left) / rect.width - 0.5
        const py = (event.clientY - rect.top) / rect.height - 0.5

        rotateY.set(px * 16)
        rotateX.set(py * -16)
    }

    const handleLeave = () => {
        rotateX.set(0)
        rotateY.set(0)
    }

    return (
        <motion.div
            ref={ref}
            onPointerMove={handleMove}
            onPointerLeave={handleLeave}
            style={{ rotateX, rotateY, transformPerspective: 1000 }}
            className={className}
        >
            {children}
        </motion.div>
    )
}

// David's headshot. Until the photo is added (see `photos` in constants/content.ts) it shows a bold monogram.
const Portrait: FC<PortraitProps> = ({ tone = 'blaze', src, className }): ReactNode => {
    const isBlaze = tone === 'blaze'
    const image = src ?? photos.headshot

    return (
        <div
            className={cn(
                'relative aspect-[4/5] overflow-hidden rounded-lg border-2 border-ink shadow-[10px_10px_0_0_var(--color-ink)]',
                image ? 'bg-[#D3FDEE] text-ink' : isBlaze ? 'bg-blaze text-ink' : 'bg-cobalt text-ink',
                className
            )}
        >
            {image ? (
                <img
                    src={image}
                    alt='David Mudry, web developer in Fort Worth, Texas'
                    className={cn('absolute inset-x-0 top-0 w-full max-w-none object-cover object-[50%_6%]', src ? 'h-[112%]' : 'h-[135%]')}
                />
            ) : null}

            {image ? (
                <div className='absolute inset-x-0 bottom-0 z-10 flex items-center justify-between border-t-2 border-ink bg-[#D3FDEE] px-5 py-4'>
                    <span className='eyebrow'>
                        David Mudry
                    </span>

                    <span aria-hidden='true' className='size-3 rounded-full bg-ink' />
                </div>
            ) : (
                <div role='img' aria-label='David Mudry' className='absolute inset-0'>
                    <div
                        className={cn(
                            'absolute inset-0 opacity-[0.14]',
                            isBlaze
                                ? '[background-image:repeating-linear-gradient(135deg,var(--color-ink)_0_2px,transparent_2px_16px)]'
                                : '[background-image:repeating-linear-gradient(135deg,var(--color-paper)_0_2px,transparent_2px_16px)]'
                        )}
                    />

                    <div className='absolute inset-0 grid place-items-center'>
                        <span className='font-heading text-[clamp(6rem,30vw,10rem)] font-extrabold leading-none tracking-[-0.06em] wdth-75 lg:text-[clamp(6rem,11vw,10rem)]'>
                            DM
                        </span>
                    </div>

                    <div className={cn('absolute inset-x-5 bottom-5 flex items-center justify-between border-t-2 pt-3', isBlaze ? 'border-ink' : 'border-paper')}>
                        <span className='eyebrow'>
                            David Mudry
                        </span>

                        <span aria-hidden='true' className={cn('size-3 rounded-full', isBlaze ? 'bg-ink' : 'bg-paper')} />
                    </div>
                </div>
            )}
        </div>
    )
}

export default Portrait
